import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const AuthContext = createContext();

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState(null);
    const [profile, setProfile] = useState(null);

    const logout = useCallback(() => {
        localStorage.removeItem('admin_session');
        localStorage.removeItem('admin_profile');
        setSession({}); // Use empty object to signify "logged out" vs initial "null"
        setProfile(null);
    }, []);

    const fetchProfile = useCallback(async () => {
        if (!session?.accessToken) return;

        try {
            const res = await fetch(`${API_BASE}/me`, {
                headers: {
                    'Authorization': session.accessToken,
                    'Vendor-Id': session.vendorId,
                }
            });
            if (res.ok) {
                const profileData = await res.json();
                
                // /me returns permissions as a flat "scope:value" array. If it's
                // ever missing/malformed, default to no permissions (deny) rather
                // than a permissive fallback — the backend is the source of truth.
                if (!profileData.permissions || !Array.isArray(profileData.permissions)) {
                    profileData.permissions = [];
                }
                
                localStorage.setItem('admin_profile', JSON.stringify(profileData));
                setProfile(profileData);
            } else {
                console.error("Failed to fetch profile:", res.status);
                if (res.status === 401 || res.status === 403) {
                    logout();
                }
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
        }
    }, [session, logout]);

    const refreshTokenIfNeeded = useCallback(async () => {
        if (!session?.refreshToken || !session.expiresAt) {
            logout();
            return false;
        }

        if (new Date(session.expiresAt) > new Date()) {
            return true; // Token is still valid.
        }

        try {
            const res = await fetch(`${API_BASE}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: session.refreshToken }),
            });

            if (!res.ok) {
                logout();
                return false;
            }

            const data = await res.json();
            const updatedSession = {
                ...session,
                accessToken: data.access_token,
                expiresAt: data.expires_at,
                refreshToken: data.refresh_token,
            };
            localStorage.setItem('admin_session', JSON.stringify(updatedSession));
            setSession(updatedSession);
            return false; // A refresh was initiated; wait for the next render.
        } catch {
            logout();
            return false;
        }
    }, [session, logout]);

    useEffect(() => {
        const storedSession = localStorage.getItem('admin_session');
        const storedProfile = localStorage.getItem('admin_profile');
        
        if (storedSession) {
            setSession(JSON.parse(storedSession));
        } else {
            setSession({}); // Mark that we've checked storage and it's empty.
        }
        
        if (storedProfile) {
            const profileData = JSON.parse(storedProfile);
            // Cached profile may pre-date real permissions; default to deny and
            // let the /me refetch populate the real "scope:value" array.
            if (!profileData.permissions || !Array.isArray(profileData.permissions)) {
                profileData.permissions = [];
            }
            setProfile(profileData);
        }
    }, []);

    useEffect(() => {
        if (session && session.accessToken) {
            const manageSession = async () => {
                const isTokenValid = await refreshTokenIfNeeded();
                if (isTokenValid) {
                    await fetchProfile();
                }
            };
            manageSession();
        }
    }, [session, fetchProfile, refreshTokenIfNeeded]);

    const login = useCallback((sessionData, vendorId) => {
        const newSession = {
            accessToken: sessionData.access_token,
            refreshToken: sessionData.refresh_token,
            expiresAt: sessionData.expires_at,
            vendorId: vendorId,
        };
        localStorage.setItem('admin_session', JSON.stringify(newSession));
        setSession(newSession);
    }, []);
    
    const isAuthenticated = session === null ? null : !!session?.accessToken;
    
    // Helper function to check permissions
    const hasPermission = useCallback((permission) => {
        if (!profile?.permissions || !Array.isArray(profile.permissions)) return false;
        return profile.permissions.includes(permission);
    }, [profile]);
    
    // Helper function to check if user has any of the given permissions
    const hasAnyPermission = useCallback((permissions) => {
        if (!profile?.permissions || !Array.isArray(profile.permissions)) return false;
        return permissions.some(permission => profile.permissions.includes(permission));
    }, [profile]);

    return (
        <AuthContext.Provider value={{ 
            isAuthenticated, 
            session, 
            profile, 
            login, 
            logout, 
            hasPermission, 
            hasAnyPermission 
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext); 